import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FormConfig, BetaFormData, FormErrors } from '../../types/form'
import FormField from '../Form/FormField'
import UIButton from '../UI/Button'
import Section from '../UI/Section'
import { submitBetaForm } from '../../utils/formSubmission'
import { loadFormConfig, validateFormConfig, getDefaultFormConfig } from '../../utils/formConfig'

const Beta: React.FC = () => {
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null)
  const [formData, setFormData] = useState<BetaFormData>({})
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submissionMessage, setSubmissionMessage] = useState('')

  useEffect(() => {
    // Load form configuration
    const initializeForm = async () => {
      try {
        const config = await loadFormConfig()
        
        if (!validateFormConfig(config)) {
          console.warn('Invalid form configuration, using default')
          setFormConfig(getDefaultFormConfig())
          return
        }
        
        setFormConfig(config)
        
        // Initialize form data with empty values
        const initialData: BetaFormData = {}
        config.fields.forEach(field => {
          switch (field.type) {
            case 'string':
              initialData[field.key] = ''
              break
            case 'integer':
              initialData[field.key] = ''
              break
            case 'array<string>':
              initialData[field.key] = []
              break
          }
        })
        setFormData(initialData)
      } catch (error) {
        console.error('Failed to load form configuration:', error)
        console.log('Using default form configuration')
        setFormConfig(getDefaultFormConfig())
      }
    }

    initializeForm()
  }, [])

  const handleFieldChange = (key: string, value: string | number | string[]) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }))
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: ''
      }))
    }
  }

  const scrollToFirstError = (errors: FormErrors) => {
    if (!formConfig) return

    // Find the first field with an error
    const firstErrorField = formConfig.fields.find(field => errors[field.key])
    if (firstErrorField) {
      // Try to find the field element by ID (works for input/textarea)
      let fieldElement = document.getElementById(firstErrorField.key)
      
      // If not found (e.g., for checkbox groups), try to find by label
      if (!fieldElement) {
        const labelElement = document.querySelector(`label[for="${firstErrorField.key}"]`)
        if (labelElement) {
          fieldElement = labelElement as HTMLElement
        }
      }
      
      // If still not found, try to find the field container
      if (!fieldElement) {
        fieldElement = document.querySelector(`[data-field-key="${firstErrorField.key}"]`) as HTMLElement
      }

      if (fieldElement) {
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
        
        // Add a temporary highlight effect
        fieldElement.style.transition = 'box-shadow 0.3s ease'
        fieldElement.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.5)'
        
        // Remove highlight after animation
        setTimeout(() => {
          if (fieldElement) {
            fieldElement.style.boxShadow = ''
          }
        }, 2000)
        
        // Focus the field for better accessibility (only if it's focusable)
        if (fieldElement.tagName === 'INPUT' || fieldElement.tagName === 'TEXTAREA') {
          fieldElement.focus()
        }
      }
    }
  }

  const validateForm = (): boolean => {
    if (!formConfig) return false

    const newErrors: FormErrors = {}
    let isValid = true

    formConfig.fields.forEach(field => {
      const value = formData[field.key]

      // Check if required field is empty
      if (field.type === 'string' && (!value || (value as string).trim() === '')) {
        newErrors[field.key] = 'This field is required'
        isValid = false
      } else if (field.type === 'integer' && (value === '' || value === undefined)) {
        newErrors[field.key] = 'This field is required'
        isValid = false
      } else if (field.type === 'array<string>' && (!Array.isArray(value) || value.length === 0)) {
        newErrors[field.key] = 'Please select at least one option'
        isValid = false
      }

      // Validate email format
      if (field.key === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value as string)) {
          newErrors[field.key] = 'Please enter a valid email address'
          isValid = false
        }
      }

      // Validate age range
      if (field.key === 'age' && value) {
        const age = Number(value)
        if (age < 13 || age > 120) {
          newErrors[field.key] = 'Please enter a valid age between 13 and 120'
          isValid = false
        }
      }
    })

    setErrors(newErrors)

    // If validation failed, scroll to the first error
    if (!isValid) {
      // Use setTimeout to ensure the errors are set before scrolling
      setTimeout(() => scrollToFirstError(newErrors), 100)
    }

    return isValid
  }

  const scrollToGeneralError = () => {
    const errorElement = document.querySelector('[data-testid="general-error"]')
    if (errorElement) {
      errorElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const result = await submitBetaForm(formData)
      
      if (result.success) {
        setIsSubmitted(true)
        setSubmissionMessage(result.message)
      } else {
        setErrors({ general: result.message })
        // Scroll to general error message
        setTimeout(() => scrollToGeneralError(), 100)
      }
    } catch (error) {
      console.error('Submission failed:', error)
      setErrors({ general: 'An unexpected error occurred. Please try again later.' })
      // Scroll to general error message
      setTimeout(() => scrollToGeneralError(), 100)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!formConfig) {
    return (
      <Section sectionId="beta" backgroundImage="city_ruins.gif">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
              <p className="mt-4 text-gray-300">Loading form...</p>
            </div>
          </div>
        </div>
      </Section>
    )
  }

  if (isSubmitted) {
    return (
      <Section sectionId="beta" backgroundImage="city_ruins.gif">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8 text-center">
            <div className="mb-8">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Application Submitted!</h2>
              
              <p className="text-gray-300 text-base sm:text-lg mb-6">
                Your beta application will be reviewed soon. We'll get back to you with updates!
              </p>

              <div className="bg-gray-800/50 rounded-lg p-4 sm:p-6 mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-red-400 mb-4">While you wait...</h3>
                
                <div className="space-y-4 sm:space-y-6">
                  {/* Discord Section */}
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="text-center sm:text-left">
                      <p className="text-gray-300 text-sm sm:text-base">
                        <span className="font-semibold text-white">Join our Discord</span> to stay updated and connect with the community
                      </p>
                    </div>
                    <a
                      href="https://discord.gg/7jDFHVJKV9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-semibold transition-colors flex items-center space-x-2 text-sm sm:text-base"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                      <span>Join Discord</span>
                    </a>
                  </div>

                  {/* Steam Section */}
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="text-center sm:text-left">
                      <p className="text-gray-300 text-sm sm:text-base">
                        <span className="font-semibold text-white">Wishlist on Steam</span> to get notified when we launch
                      </p>
                    </div>
                    <a
                      href="https://store.steampowered.com/app/3423700/PL4noB/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-semibold transition-colors flex items-center space-x-2 text-sm sm:text-base"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.624 0 11.99-5.366 11.99-11.99C23.97 5.38 18.604.001 11.979.001zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54z"/>
                      </svg>
                      <span>Wishlist on Steam</span>
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-gray-400 text-sm">
                Keep an eye on your email and Discord for beta testing updates!
              </p>
            </div>
          </div>
        </div>
      </Section>
    )
  }

  return (
    <Section sectionId="beta" backgroundImage="city_ruins.gif">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
              {formConfig.title}
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed px-2 sm:px-0">
              {formConfig.description}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* General Error Message */}
            {errors.general && (
              <div 
                className="bg-red-900/50 border border-red-500 rounded-md p-4"
                data-testid="general-error"
              >
                <p className="text-red-200 text-center">{errors.general}</p>
              </div>
            )}

            {formConfig.fields.map((field) => (
              <FormField
                key={field.key}
                field={field}
                value={formData[field.key] || (field.type === 'array<string>' ? [] : '')}
                onChange={handleFieldChange}
                error={errors[field.key]}
              />
            ))}

            {/* ToS Agreement */}
            <div className="pt-4 sm:pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-xs sm:text-sm text-center mb-4 px-2 sm:px-0">
                By submitting this application, you agree to our{' '}
                <Link 
                  to="/tos" 
                  className="text-red-400 hover:text-red-300 underline transition-colors"
                  target="_blank"
                >
                  Terms of Service
                </Link>
                {' '}and confirm that you will follow our community guidelines and beta testing rules.
              </p>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700 hover:text-white'} bg-red-600 text-white px-6 sm:px-8 py-2 sm:py-3 text-lg sm:text-xl font-bold shadow-lg border-2 border-red-500 hover:scale-100 w-full sm:w-auto rounded-md transition duration-300`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>

          {/* Footer Note */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-700">
            <p className="text-gray-400 text-xs sm:text-sm text-center px-2 sm:px-0">
              Questions about the beta program? Contact us at{' '}
              <a 
                href="mailto:olivato@plan-b4.com" 
                className="text-red-400 hover:text-red-300 underline transition-colors"
              >
                beta@plan-b4.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </Section>
  )
}

export default Beta
